/**
 * EntityForm Component
 *
 * Render-prop wrapper that removes the boilerplate shared by every entity
 * create/edit form: it owns the `useForm` setup (Zod resolver + default values),
 * the `reset`-on-`defaultValues`-change effect for edit-mode data loading, the
 * `<form>` element, and the standard Cancel / Submit action row.
 *
 * Each form supplies only its own field sections via the render-prop children,
 * which receive the full React Hook Form API plus the watched `values`.
 */

import { useEffect, useRef } from 'react';
import { useForm, type DefaultValues, type FieldValues, type UseFormReturn } from 'react-hook-form';
import { type z } from 'zod';
import { createZodResolver } from '../forms/validation';
import '@tylertech/forge/button';
import '@tylertech/forge/card';

/** React Hook Form API handed to the render-prop children, plus watched values. */
export interface EntityFormApi<T extends FieldValues> extends UseFormReturn<T> {
  /** Result of `watch()` — current form values for controlled inputs. */
  values: T;
}

export interface EntityFormProps<T extends FieldValues> {
  /** Zod schema used to build the resolver. */
  schema: z.ZodType<T>;
  /** Default values used in create mode (and merged under edit values). */
  baseValues: DefaultValues<T>;
  /** Initial values for edit mode; triggers a `reset` when its reference changes. */
  defaultValues?: Partial<T>;
  /** Submit handler invoked with validated, parsed data. */
  onSubmit: (data: T) => void | Promise<void>;
  /** Cancel handler. */
  onCancel: () => void;
  /** Disables the action buttons while a submit is in flight. */
  isSubmitting?: boolean;
  /** Form mode — controls the submit button label. */
  mode: 'create' | 'edit';
  /** Submit button label in create mode (e.g. "Create Team"). */
  createLabel: string;
  /** Submit button label in edit mode. Defaults to "Save Changes". */
  editLabel?: string;
  /** Class applied to the `<form>` element. */
  formClassName?: string;
  /** Class applied to the action-row container. Defaults to "form-actions". */
  actionsClassName?: string;
  /** Disables native browser validation on the form. */
  noValidate?: boolean;
  /** Render-prop receiving the form API. */
  children: (api: EntityFormApi<T>) => React.ReactNode;
}

const ForgeButton = 'forge-button' as React.ElementType;

/**
 * Shared create/edit form wrapper.
 *
 * @example
 * ```tsx
 * <EntityForm
 *   schema={createServiceSchema}
 *   baseValues={{ lifecycleStatus: 'development', tier: 'tier2' }}
 *   defaultValues={defaultValues}
 *   onSubmit={onSubmit}
 *   onCancel={onCancel}
 *   isSubmitting={isSubmitting}
 *   mode={mode}
 *   createLabel="Create Service"
 *   formClassName="service-form"
 * >
 *   {({ register, formState: { errors }, values }) => (
 *     <forge-card>{/* field sections *\/}</forge-card>
 *   )}
 * </EntityForm>
 * ```
 */
export function EntityForm<T extends FieldValues>({
  schema,
  baseValues,
  defaultValues,
  onSubmit,
  onCancel,
  isSubmitting = false,
  mode,
  createLabel,
  editLabel = 'Save Changes',
  formClassName,
  actionsClassName = 'form-actions',
  noValidate,
  children,
}: EntityFormProps<T>): JSX.Element {
  const methods = useForm<T>({
    resolver: createZodResolver(schema),
    defaultValues: defaultValues
      ? ({ ...baseValues, ...defaultValues } as DefaultValues<T>)
      : baseValues,
  });

  const { handleSubmit, reset, watch } = methods;

  // Keep the latest baseValues without making it an effect dependency, so the
  // reset effect fires only when the defaultValues prop reference changes
  // (matching the original per-form behavior and avoiding a reset loop).
  const baseValuesRef = useRef(baseValues);
  baseValuesRef.current = baseValues;

  useEffect(() => {
    if (defaultValues) {
      reset({ ...baseValuesRef.current, ...defaultValues } as DefaultValues<T>);
    }
  }, [defaultValues, reset]);

  const values = watch();

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={formClassName} noValidate={noValidate}>
      {children({ ...methods, values })}

      <div className={actionsClassName}>
        <ForgeButton type="button" variant="outlined" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </ForgeButton>
        <ForgeButton type="submit" variant="raised" disabled={isSubmitting}>
          {mode === 'create' ? createLabel : editLabel}
        </ForgeButton>
      </div>
    </form>
  );
}
